import LeadForm from '@/components/shared/LeadForm';
import PricingSection from '@/components/shared/PricingSection';
import TestimonialSlider from '@/components/shared/TestimonialSlider';
import CTABanner from '@/components/shared/CTABanner';
import SEO from '@/components/shared/SEO';
import { heroImages } from '@/data/images';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { ShieldCheck, Clock, Users, Award, CheckCircle, FileText, Lock, ArrowRight, Globe, HeadphonesIcon } from 'lucide-react';
import { usePopup } from '@/contexts/PopupContext';

const benefits = [
  { icon: ShieldCheck, title: 'Nationwide Protection', desc: 'Secure exclusive trademark rights for your brand name, logo, and business identity. Protect your brand across all 50 states with federal registration.' },
  { icon: Lock, title: 'Legal Protection', desc: 'Gain stronger legal rights to defend your trademark against infringement. Take action against unauthorized use that may create marketplace confusion.' },
  { icon: Globe, title: 'Online Brand Security', desc: 'Strengthen your brand presence across online marketplaces and digital platforms. Support brand protection programs and safeguard your online reputation.' },
  { icon: Award, title: 'Business Growth', desc: 'A registered trademark increases trust, credibility, and brand recognition. It also adds value to your business as a protected intellectual property asset.' },
  { icon: FileText, title: 'Complete Filing Records', desc: 'Receive organized documentation for your trademark registration and filings. Keep a clear record of your application status and registration history.' },
  { icon: HeadphonesIcon, title: 'Dedicated Assistance', desc: 'Work with experienced trademark specialists throughout the registration process. Get reliable support and updates from filing to final registration.' },
];

const guarantees = [
  { title: 'Satisfaction Guarantee', desc: 'If we cannot file your trademark, you will receive a full refund.' },
  { title: 'Expert Filing', desc: 'Every application is reviewed by experienced trademark professionals.' },
  { title: 'Secure Process', desc: 'Your information is protected with bank-level encryption and security.' },
  { title: 'No Hidden Fees', desc: 'Transparent pricing with no surprise charges or hidden costs.' },
];

const lpFaqs = [
  { q: 'How long does trademark registration take?', a: 'The typical timeline from filing to registration is 8-12 months. Our team monitors your application throughout the process and responds to any issues promptly.' },
  { q: 'What is included in the filing fee?', a: 'Our service fee covers comprehensive trademark search, application preparation, filing with the USPTO, and ongoing monitoring of your application status.' },
  { q: 'Can I trademark a name that is already in use?', a: 'It depends on the industry and geographic scope. A clearance search will determine if your desired trademark is available and the risks associated with registration.' },
  { q: 'What if my application is rejected?', a: 'Most office actions can be overcome. Our team reviews the specific issues and crafts strategic responses. Enhanced and higher plans include office action support.' },
  { q: 'Do I need a trademark for my small business?', a: 'Absolutely. Trademark protection is essential for businesses of all sizes. It prevents competitors from using similar names and gives you the legal tools to protect your brand.' },
  { q: 'What is the difference between TM and the registered symbol?', a: 'TM can be used anytime to claim trademark rights. The registered symbol can only be used after your mark is officially registered with the USPTO.' },
];

const mediaLogos = ['Forbes', 'Bloomberg', 'Reuters', 'TechCrunch', 'Inc.', 'Entrepreneur'];

export default function PPCLandingPage() {
  const { openPopup } = usePopup();

  return (
    <div data-testid="ppc-landing-page">
      <SEO
        title="Register Your Trademark Today - Starting at $45"
        description="Register your trademark with the #1 rated service. Federal protection across all 50 states starting at just $45 + filing fee. Expert guidance, fast processing, 98% success rate."
      />
      {/* 1. Hero with Form */}
      <section className="relative pt-28 pb-12 lg:pt-28 lg:pb-24 overflow-hidden" data-testid="lp-hero">
        <div className="absolute inset-0 z-0">
          <img src={heroImages.lp} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#064E3B]/95 via-[#064E3B]/75 to-[#064E3B]/30" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-6 tracking-wide border border-white/20">
                <ShieldCheck className="w-3.5 h-3.5" /> #1 TRADEMARK REGISTRATION SERVICE
              </div>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-white leading-tight tracking-tight mb-6">
                Claim Your Brand Before Someone Else Does
              </h1>
              <p className="text-emerald-100 text-base lg:text-lg leading-relaxed mb-8">
                Every day without trademark protection is a day your brand is vulnerable. Secure federal registration starting at just $45 + filing fees. Our experts handle the entire process from search to approval.
              </p>
              <div className="flex items-center gap-6 text-sm text-emerald-200">
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-300" /> 98% Success Rate</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-300" /> Fast Filing</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-300" /> Expert Support</div>
              </div>
            </div>
            <div className="order-1 md:order-2 bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden md:mt-16" data-testid="lp-hero-form">
              <div className="bg-[#064E3B] px-6 py-5">
                <h2 className="font-heading font-bold text-lg text-white">Trademark Your Business Identity</h2>
                <p className="text-emerald-200 text-sm">Protect your brand with expert trademark registration.</p>
              </div>
              <div className="p-6">
                <LeadForm packageTitle="Essentials" packageAmount={45} embedded={true} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Informative Section */}
      <section className="py-12 lg:py-28 bg-white" data-testid="lp-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#10B981] font-semibold text-sm tracking-wider uppercase">Act Now</span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#064E3B] mt-3 mb-6">
                Your Competitors Are Already Filing. Are You?
              </h2>
              <p className="text-slate-600 leading-relaxed mb-5">
                Your brand name and logo represent years of hard work, customer trust, and market positioning. Without federal trademark registration, anyone can legally use a similar mark — and there's little you can do to stop them.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                A registered trademark gives you the exclusive right to use your mark nationwide and the legal authority to enforce that right. It's the foundation of brand protection.
              </p>
              <div className="space-y-3">
                {['Prevent competitors from copying your brand', 'Build lasting brand equity and business value', 'Qualify for Amazon Brand Registry and platform protections'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0" />
                    <span className="text-sm text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7108454/pexels-photo-7108454.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Brand protection consultation"
                className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 3. Services Details */}
      <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="lp-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#064E3B] mb-4">
             6 Benefits of Our Trademark Registration Services
            </h2>
            <p className="text-slate-600 text-base lg:text-lg max-w-2xl mx-auto">
              Professional trademark registration services provide valuable protection and advantages for growing businesses.
            </p>
          </div>
          <div className="relative px-8">
            <Carousel opts={{ align: 'start', loop: true }}>
              <CarouselContent className="-ml-4">
                {benefits.map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <CarouselItem key={i} className="pl-4 basis-[100%] md:basis-[50%] lg:basis-[33.333%]">
                      <div className="p-6 bg-white rounded-2xl border border-emerald-100 card-hover h-full" data-testid={`lp-benefit-${i}`}>
                        <Icon className="w-8 h-8 text-[#10B981] mb-4" />
                        <h3 className="font-heading font-bold text-base text-[#064E3B] mb-2">{b.title}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="border-emerald-200 text-[#064E3B]" />
              <CarouselNext className="border-emerald-200 text-[#064E3B]" />
            </Carousel>
          </div>
        </div>
      </section>



      {/* 6. Pricing Plans */}
      <PricingSection />

      {/* 7. Why Choose Us */}
      <section className="py-12 lg:py-28 bg-white" data-testid="lp-why-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#064E3B] mb-4">
              The Trademark Pact Advantage
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#064E3B]" />
              </div>
              <h3 className="font-heading font-bold text-lg text-[#064E3B] mb-2">Expert Team</h3>
              <p className="text-sm text-slate-600">Experienced trademark professionals dedicated to protecting your brand with precision and care.</p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-[#064E3B]" />
              </div>
              <h3 className="font-heading font-bold text-lg text-[#064E3B] mb-2">Fast Turnaround</h3>
              <p className="text-sm text-slate-600">Streamlined processes ensure your application is filed quickly and accurately every time.</p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-[#064E3B]" />
              </div>
              <h3 className="font-heading font-bold text-lg text-[#064E3B] mb-2">Proven Results</h3>
              <p className="text-sm text-slate-600">Thousands of successful registrations with consistently high approval rates nationwide.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Testimonials */}
      <TestimonialSlider />

      {/* 9. FAQ */}
      <section className="py-12 lg:py-28 bg-[#F0FDF4]" data-testid="lp-faq-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#064E3B] mb-8">
                Common Questions
              </h2>
              <div className="space-y-3">
                {lpFaqs.map((faq, i) => (
                  <details key={i} className="bg-white rounded-xl border border-emerald-100 group" data-testid={`lp-faq-${i}`}>
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-heading font-semibold text-sm text-[#064E3B] list-none">
                      {faq.q}
                      <span className="text-[#10B981] text-lg transition-transform group-open:rotate-45">+</span>
                    </summary>
                    <div className="px-6 pb-4 text-sm text-slate-600 leading-relaxed">{faq.a}</div>
                  </details>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format"
                alt="FAQ support"
                className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 10. CTA Banner */}
      <CTABanner showChat={true} />
    </div>
  );
}
